import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { camelCase } from 'change-case/keys'
import type { User, UserRole, NavItem } from '@/types'
import type { NotificationItem, NotificationSummary } from '@/types/notification'
import { menuList } from '@/data/menu'
import { roleMenuMapping, publicMenus } from '@/data/role_menu_mapping'
import { API_URL } from '@/constants/api'
import { ERROR_TYPE } from '@/constants/consts'
import apiHelper from '@/utils/apiHelper'
import { useAccountStore } from './account'
import { useMenuStore } from './menu'
import { $alert } from '@/plugins/simple-alert'

const UNIFIED_ACCENT = { color: '#06B6D4', hover: '#0891B2', rgb: '6, 182, 212' }

const ROLE_ACCENT_MAP: Record<
  UserRole,
  { color: string; hover: string; rgb: string; label: string }
> = {
  ADMIN: { ...UNIFIED_ACCENT, label: 'Super 관리자' },
  PROJECT_MANAGER: { ...UNIFIED_ACCENT, label: '프로젝트 관리자' },
  RESEARCHER: { ...UNIFIED_ACCENT, label: '연구자' },
  COOPERATION_PARTNER: { ...UNIFIED_ACCENT, label: '협력 파트너' },
}

const MENU_META: Record<string, { path: string; icon: string }> = {
  HOME: { path: '/', icon: 'Home' },
  DASHBOARD: { path: '/dashboard', icon: 'LayoutDashboard' },
  MONITORING: { path: '/monitoring', icon: 'Monitor' },
  PROJECT: { path: '/projects', icon: 'LayoutList' },
  EXPLORER: { path: '/explorer', icon: 'Database' },
  WORKSPACE: { path: '/workspace', icon: 'FolderOpen' },
  ABOUT: { path: '/about', icon: 'Building2' },
  GUIDE: { path: '/guide', icon: 'BookOpen' },
  QNA: { path: '/qna', icon: 'MessageSquare' },
  MANAGEMENT: { path: '/management', icon: 'Settings2' },
}

export const useUserStore = defineStore('user', () => {
  const accountStore = useAccountStore()
  const menuStore = useMenuStore()

  // 로컬 스토리지에서 세션 복원
  const savedUser = localStorage.getItem('user_session')
  const initialUser = savedUser ? (JSON.parse(savedUser) as User) : null

  const isAuthenticated = ref<boolean>(!!initialUser || !!accountStore.accessToken)
  const currentUser = ref<User>(
    initialUser || {
      name: accountStore.userName || '',
      email: accountStore.userId || '',
      role: accountStore.roleId ? ((accountStore.roleId.toUpperCase() as UserRole)) : '',
    },
  )

  const isShowUserMenu = ref<boolean>(false)
  const isShowNotifications = ref<boolean>(false)
  const notifications = ref<NotificationItem[]>([])
  const unreadNotificationCount = ref<number>(0)
  const isFetchingNotifications = ref<boolean>(false)

  let sseSource: EventSource | null = null

  const navItems = computed((): NavItem[] => {
    const role = isAuthenticated.value
      ? ((currentUser.value.role || accountStore.roleId || '').toUpperCase() as UserRole)
      : null

    const apiMenus = menuStore.menus
    if (apiMenus && apiMenus.length > 0) {
      return apiMenus
        .filter((m) => m.useYn !== 'N')
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map((m) => ({
          id: m.menuId,
          label: m.menuName,
          path: MENU_META[m.menuId]?.path || m.menuUrl || '/',
          icon: MENU_META[m.menuId]?.icon || 'Circle',
        }))
    }

    if (!isAuthenticated.value) {
      return []
    }

    const allowedMenuIds = (role && roleMenuMapping[role]) ? roleMenuMapping[role] : []
    return menuList
      .filter((m) => allowedMenuIds.includes(m.menuId as any))
      .sort((a, b) => a.menuSeq - b.menuSeq)
      .map((m) => ({
        id: m.menuId,
        label: m.menuName,
        path: MENU_META[m.menuId]?.path || '/',
        icon: MENU_META[m.menuId]?.icon || 'Circle',
      }))
  })

  const userInitials = computed<string>(() => {
    return (currentUser.value.name || 'U')
      .split(' ')
      .map((n) => n[0])
      .join('')
  })

  const userShortName = computed<string>(() => {
    return currentUser.value.name?.split(' ')[1] || currentUser.value.name || 'User'
  })

  const roleAccentColor = computed(() => {
    const role = (currentUser.value.role || '').toUpperCase() as UserRole
    return (role && ROLE_ACCENT_MAP[role]) ? ROLE_ACCENT_MAP[role] : UNIFIED_ACCENT
  })

  function connectNotificationSSE(): void {
    if (!isAuthenticated.value) return
    if (sseSource) {
      sseSource.close()
      sseSource = null
    }

    const token = accountStore.accessToken || localStorage.getItem('access_token') || ''
    if (!token) return

    try {
      const url = `${API_URL.NOTIFICATION.STREAM}?token=${encodeURIComponent(token)}`
      sseSource = new EventSource(url)

      const handleNotificationPayload = (dataStr: string) => {
        if (!dataStr) return
        try {
          // SSE 는 apiHelper 를 거치지 않으므로 서버의 snake_case 키를 여기서 camelCase 로 바꾼다.
          const newNotif = camelCase(JSON.parse(dataStr), -1) as NotificationItem
          if (!newNotif || !newNotif.id) return
          if (newNotif.delYn === 'Y') return

          const exists = notifications.value.some((n) => n.id === newNotif.id)
          if (!exists) {
            const readStatus = newNotif.readYn ?? newNotif.isRead ?? 'N'
            notifications.value.unshift({
              ...newNotif,
              readYn: readStatus,
              isRead: readStatus,
            })
            if (readStatus === 'N') {
              unreadNotificationCount.value += 1
              // 실시간 도착 알림 토스트 표시
              if (newNotif.message) {
                if (newNotif.type === 'DOI_REQUEST') {
                  $alert.info(newNotif.message, 'DOI 발급 심사 대기')
                } else if (newNotif.type === 'DOI_APPROVED') {
                  $alert.success(newNotif.message, 'DOI 발급 승인')
                } else if (newNotif.type === 'DOI_REJECTED') {
                  $alert.warn(newNotif.message, 'DOI 발급 반려')
                } else {
                  $alert.info(newNotif.message)
                }
              }
            }
          }
        } catch (err) {
          console.error('Failed to parse SSE notification payload:', err)
        }
      }

      sseSource.addEventListener('notification', (event) => {
        handleNotificationPayload(event.data)
      })

      sseSource.onmessage = (event) => {
        handleNotificationPayload(event.data)
      }

      sseSource.addEventListener('connected', () => {
        console.log('[SSE] Notification stream connected successfully')
      })

      sseSource.onerror = (err) => {
        console.warn('[SSE] Notification stream error / reconnecting...', err)
      }
    } catch (err) {
      console.error('[SSE] Failed to initialize EventSource:', err)
    }
  }

  function disconnectNotificationSSE(): void {
    if (!sseSource) return
    sseSource.close()
    sseSource = null
  }

  async function login(id: string, pw: string): Promise<{ success: boolean; errorMsg?: string }> {
    try {
      const res = await apiHelper.post<{
        accessToken?: string
        access_token?: string
        refreshToken?: string
        refresh_token?: string
        seaweedfsUrl?: string
        seaweedfs_url?: string
      }>(API_URL.AUTH.LOGIN, { id, pw })

      if (res && res.errorCode === 0 && res.data) {
        const accessToken = res.data.accessToken || ''
        const refreshToken = res.data.refreshToken || ''
        const seaweedfsUrl = res.data.seaweedfsUrl

        accountStore.setSession({
          accessToken,
          refreshToken,
          seaweedfsUrl,
          data: null,
        })
        await accountStore.fetchMenus()

        isAuthenticated.value = true
        const role = ((accountStore.roleId || '').toUpperCase() as UserRole)
        currentUser.value = {
          name: accountStore.userName || id,
          email: accountStore.userId || id,
          role,
        }

        localStorage.setItem('user_session', JSON.stringify(currentUser.value))
        isShowUserMenu.value = false
        return { success: true }
      }

      const errorMsg =
        res?.errorCode === ERROR_TYPE.INVALID_ACCOUNT_ERROR.errorCode || res?.errorCode === 20001
          ? '아이디 또는 비밀번호가 일치하지 않습니다.'
          : res?.errorMsg || '로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.'
      return {
        success: false,
        errorMsg,
      }
    } catch (e: unknown) {
      const err = e as { errorMsg?: string }
      return {
        success: false,
        errorMsg: err?.errorMsg || '아이디 또는 비밀번호가 일치하지 않습니다.',
      }
    }
  }

  function logout(): void {
    accountStore.delSession()
    isAuthenticated.value = false
    currentUser.value = {
      name: '',
      email: '',
      role: '',
    }
    localStorage.removeItem('user_session')
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    sessionStorage.removeItem('last_valid_path')
    sessionStorage.clear()
    disconnectNotificationSSE()
    notifications.value = []
    unreadNotificationCount.value = 0
    isShowUserMenu.value = false
    isShowNotifications.value = false
  }

  function setRole(newRole: UserRole): void {
    currentUser.value = { ...currentUser.value, role: newRole }
  }

  async function fetchNotifications(): Promise<void> {
    if (!isAuthenticated.value) return
    isFetchingNotifications.value = true
    try {
      const res = await apiHelper.get<NotificationSummary>(API_URL.NOTIFICATION.LIST)
      if (res && res.errorCode === 0 && res.data) {
        notifications.value = (res.data.notifications || []).map((n) => ({
          ...n,
          readYn: n.readYn ?? n.isRead ?? 'N',
          isRead: n.readYn ?? n.isRead ?? 'N',
        }))
        unreadNotificationCount.value =
          res.data.unreadCount ?? notifications.value.filter((n) => (n.readYn ?? n.isRead) === 'N').length
      }
      if (!sseSource) {
        connectNotificationSSE()
      }
    } catch (e) {
      console.error('Failed to fetch notifications:', e)
    } finally {
      isFetchingNotifications.value = false
    }
  }

  async function markNotificationAsRead(id: number): Promise<void> {
    try {
      const res = await apiHelper.put(API_URL.NOTIFICATION.READ(id), {})
      if (res && res.errorCode === 0) {
        const item = notifications.value.find((n) => n.id === id)
        if (item && (item.isRead === 'N' || item.readYn === 'N')) {
          item.isRead = 'Y'
          item.readYn = 'Y'
          if (unreadNotificationCount.value > 0) {
            unreadNotificationCount.value -= 1
          }
        }
      }
    } catch (e) {
      console.error('Failed to mark notification as read:', e)
    }
  }

  async function markAllNotificationsAsRead(): Promise<void> {
    try {
      const res = await apiHelper.put(API_URL.NOTIFICATION.READ_ALL, {})
      if (res && res.errorCode === 0) {
        notifications.value.forEach((n) => {
          n.isRead = 'Y'
          n.readYn = 'Y'
        })
        unreadNotificationCount.value = 0
      }
    } catch (e) {
      console.error('Failed to mark all notifications as read:', e)
    }
  }

  async function deleteNotification(id: number): Promise<void> {
    try {
      const res = await apiHelper.delete(API_URL.NOTIFICATION.DELETE(id))
      if (res && res.errorCode === 0) {
        const item = notifications.value.find((n) => n.id === id)
        if (item && item.isRead === 'N' && unreadNotificationCount.value > 0) {
          unreadNotificationCount.value -= 1
        }
        notifications.value = notifications.value.filter((n) => n.id !== id)
      }
    } catch (e) {
      console.error('Failed to delete notification:', e)
    }
  }

  async function deleteReadNotifications(): Promise<void> {
    try {
      const res = await apiHelper.delete(API_URL.NOTIFICATION.DELETE_READ)
      if (res && res.errorCode === 0) {
        notifications.value = notifications.value.filter((n) => n.isRead === 'N')
      }
    } catch (e) {
      console.error('Failed to delete read notifications:', e)
    }
  }

  return {
    isAuthenticated,
    currentUser,
    isShowUserMenu,
    isShowNotifications,
    notifications,
    unreadNotificationCount,
    isFetchingNotifications,
    fetchNotifications,
    connectNotificationSSE,
    disconnectNotificationSSE,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    deleteReadNotifications,
    login,
    logout,
    setRole,
    navItems,
    userInitials,
    userShortName,
    roleAccentColor,
  }
})
